import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { FlagAgent, FlagState } from '../../types';

interface FlagAgentsState {
  agents: FlagAgent[];
  selectedAgentId: string | null;
  selectedAgentStates: FlagState[];
  loading: boolean;
  error: string | null;
  createModalOpen: boolean;
  editModalOpen: boolean;
  editingAgent: FlagAgent | null;
  runningAgentId: string | null;
}

const initialState: FlagAgentsState = {
  agents: [],
  selectedAgentId: null,
  selectedAgentStates: [],
  loading: false,
  error: null,
  createModalOpen: false,
  editModalOpen: false,
  editingAgent: null,
  runningAgentId: null,
};

const flagAgentsSlice = createSlice({
  name: 'flagAgents',
  initialState,
  reducers: {
    setAgents(state, action: PayloadAction<FlagAgent[]>) {
      state.agents = action.payload;
    },
    addAgent(state, action: PayloadAction<FlagAgent>) {
      state.agents.unshift(action.payload);
    },
    updateAgent(state, action: PayloadAction<FlagAgent>) {
      const idx = state.agents.findIndex(a => a.id === action.payload.id);
      if (idx >= 0) {
        state.agents[idx] = action.payload;
      }
    },
    removeAgent(state, action: PayloadAction<string>) {
      state.agents = state.agents.filter(a => a.id !== action.payload);
      if (state.selectedAgentId === action.payload) {
        state.selectedAgentId = null;
        state.selectedAgentStates = [];
      }
    },
    setSelectedAgentId(state, action: PayloadAction<string | null>) {
      state.selectedAgentId = action.payload;
      if (!action.payload) {
        state.selectedAgentStates = [];
      }
    },
    setSelectedAgentStates(state, action: PayloadAction<FlagState[]>) {
      state.selectedAgentStates = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    openCreateModal(state) {
      state.createModalOpen = true;
    },
    closeCreateModal(state) {
      state.createModalOpen = false;
    },
    openEditModal(state, action: PayloadAction<FlagAgent>) {
      state.editModalOpen = true;
      state.editingAgent = action.payload;
    },
    closeEditModal(state) {
      state.editModalOpen = false;
      state.editingAgent = null;
    },
    setRunningAgentId(state, action: PayloadAction<string | null>) {
      state.runningAgentId = action.payload;
    },
  },
});

export const {
  setAgents,
  addAgent,
  updateAgent,
  removeAgent,
  setSelectedAgentId,
  setSelectedAgentStates,
  setLoading,
  setError,
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  setRunningAgentId,
} = flagAgentsSlice.actions;

export default flagAgentsSlice.reducer;
